import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Bot, User, Save, ClipboardList, LogIn, Calendar, Clock, ChevronRight, Check, ArrowLeft, HelpCircle } from 'lucide-react';
import MedicalAIAssistant from './MedicalAIAssistant';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Simple markdown to HTML converter for chat messages
const renderMarkdown = (text) => {
  if (!text) return '';
  
  // Convert **bold** to <strong>
  let html = text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  
  // Convert *italic* to <em>
  html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
  
  // Convert --- to <hr>
  html = html.replace(/^---$/gm, '<hr class="my-3 border-gray-200">');
  
  // Convert line breaks
  html = html.replace(/\n/g, '<br>');
  
  return html;
};

export const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [mode, setMode] = useState('general'); // 'anamnesis', 'general', 'booking', or 'faq'
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [hasReport, setHasReport] = useState(false);
  const [lastReport, setLastReport] = useState('');
  const [saving, setSaving] = useState(false);
  const [visitReason, setVisitReason] = useState(null); // For smart anamnesis
  const [showReasonSelector, setShowReasonSelector] = useState(false);
  const anamnesisStartedRef = useRef(false); // Usa ref per evitare problemi di closure
  const [quickReplies, setQuickReplies] = useState([]); // Smart suggestions
  const [isMobile, setIsMobile] = useState(false);
  
  // Booking state
  const [bookingStep, setBookingStep] = useState(null); // null, 'date', 'time', 'reason', 'confirm'
  const [availableDates, setAvailableDates] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedReason, setSelectedReason] = useState('');
  const [bookingLoading, setBookingLoading] = useState(false);
  
  const messagesEndRef = useRef(null);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Listen for open event from Navbar
  useEffect(() => {
    const handleOpenChat = () => {
      setIsOpen(true);
    };
    window.addEventListener('openChatWidget', handleOpenChat);
    return () => window.removeEventListener('openChatWidget', handleOpenChat);
  }, []);

  const BOOKING_REASONS = [
    'Visita di controllo',
    'Prima visita',
    'Problemi di vista',
    'Occhio rosso/irritato',
    'Cataratta',
    'Glaucoma',
    'Maculopatia',
    'Chirurgia refrattiva',
    'Altro'
  ];

  const ANAMNESIS_REASONS = [
    { id: 'controllo', label: '🔍 Controllo di routine', icon: '🔍' },
    { id: 'vista', label: '👓 Problemi di vista', icon: '👓' },
    { id: 'occhio_rosso', label: '🔴 Occhio rosso/irritato', icon: '🔴' },
    { id: 'mal_testa', label: '🤕 Mal di testa visivo', icon: '🤕' },
    { id: 'cataratta', label: '🌫️ Cataratta', icon: '🌫️' },
    { id: 'glaucoma', label: '👁️ Glaucoma', icon: '👁️' },
    { id: 'maculopatia', label: '🎯 Maculopatia', icon: '🎯' },
    { id: 'altro', label: '📋 Altro', icon: '📋' }
  ];

  // Generate smart quick replies based on the last bot message
  const generateQuickReplies = (botMessage) => {
    if (!botMessage) return [];
    const msg = botMessage.toLowerCase();
    
    // Common Yes/No questions
    if (msg.includes('sì o no') || msg.includes('si o no') || 
        msg.includes('hai') && msg.includes('?') ||
        msg.includes('soffre') || msg.includes('assume') ||
        msg.includes('prende') || msg.includes('usa')) {
      return ['Sì', 'No'];
    }
    
    // Name question
    if (msg.includes('come si chiama') || msg.includes('il tuo nome') || msg.includes('come ti chiami')) {
      return [];
    }
    
    // Age question
    if (msg.includes('età') || msg.includes('anni hai') || msg.includes('quanti anni')) {
      return [];
    }
    
    // How did you find us
    if (msg.includes('conosciuto') || msg.includes('trovato') || msg.includes('saputo di noi')) {
      return ['Passaparola', 'Google', 'Social Media', 'Medico di base', 'Altro'];
    }
    
    // Work/profession
    if (msg.includes('lavoro') || msg.includes('professione') || msg.includes('occupazione')) {
      return ['Impiegato/a', 'Studente', 'Pensionato/a', 'Casalinga/o', 'Altro'];
    }
    
    // Born at term
    if (msg.includes('termine') || msg.includes('nascita') || msg.includes('nato')) {
      return ['Sì, a termine', 'No, prematuro/a'];
    }
    
    // Diabetes
    if (msg.includes('diabete') || msg.includes('diabetico')) {
      return ['No', 'Sì, tipo 1', 'Sì, tipo 2'];
    }
    
    // Allergies
    if (msg.includes('allergi')) {
      return ['Nessuna', 'Sì, a farmaci', 'Sì, alimentari', 'Sì, stagionali'];
    }
    
    // Glasses/contacts
    if (msg.includes('occhiali') || msg.includes('lenti')) {
      return ['Sì', 'No', 'Solo per leggere', 'Solo da lontano'];
    }
    
    // Family history
    if (msg.includes('familiarità') || msg.includes('famiglia') || msg.includes('parenti')) {
      return ['No', 'Sì, genitori', 'Sì, fratelli', 'Sì, nonni'];
    }
    
    // Stress level
    if (msg.includes('stress') || msg.includes('stressante')) {
      return ['No', 'Sì, lieve', 'Sì, moderato', 'Sì, elevato'];
    }
    
    // Scale questions (1-10)
    if (msg.includes('da 1 a 10') || msg.includes('scala')) {
      return ['1-2 (lieve)', '3-4', '5-6', '7-8', '9-10 (grave)'];
    }
    
    // Confirmation
    if (msg.includes('confermo') || msg.includes('corretto')) {
      return ['CONFERMO', 'Voglio modificare'];
    }
    
    return [];
  };

  // Check login status
  useEffect(() => {
    const checkLogin = () => {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      if (token && userData) {
        const user = JSON.parse(userData);
        setIsLoggedIn(true);
        setUserRole(user.role);
        if (user.role === 'patient') {
          setMode('anamnesis');
          // Non mostrare più il selettore preimpostato
          setShowReasonSelector(false);
        }
      } else {
        setIsLoggedIn(false);
        setUserRole(null);
        setMode('general');
      }
    };
    
    checkLogin();
    window.addEventListener('storage', checkLogin);
    const interval = setInterval(checkLogin, 2000);
    
    return () => {
      window.removeEventListener('storage', checkLogin);
      clearInterval(interval);
    };
  }, []);

  const getInitialMessage = (selectedMode) => {
    if (selectedMode === 'anamnesis' && isLoggedIn && userRole === 'patient') {
      return {
        role: 'assistant',
        content: 'Benvenuto nel servizio di Anamnesi PRIME VISION! 👋\n\nPer preparare al meglio la tua visita, ti farò alcune domande.\n\n**Come ti chiami?**'
      };
    }
    if (selectedMode === 'booking') {
      return {
        role: 'assistant',
        content: '📅 **Prenota il tuo appuntamento**\n\nPuoi prenotare direttamente dalla nostra pagina dedicata:\n\n👉 **[Clicca qui per prenotare](/booking)**\n\nOppure dimmi come posso aiutarti!',
        showBookingLink: true
      };
    }
    if (selectedMode === 'faq') {
      return {
        role: 'assistant',
        content: '📚 **FAQ - Domande Frequenti**\n\nEcco le domande più comuni:\n\n' +
          '1️⃣ **Orari**: Lun-Ven 8:30-19:30, 2 Sabati al mese 9:30-12:30\n' +
          '2️⃣ **Prenotazioni**: Online sul sito, telefonicamente o tramite app\n' +
          '3️⃣ **Abbonamenti**: Base €59/mese, Premium €99/mese (singoli o familiari fino a 5 persone)\n' +
          '4️⃣ **Documenti**: Porta tessera sanitaria e eventuali esami precedenti\n' +
          '5️⃣ **Disdette e Spostamenti**: Puoi spostare o disdire il tuo appuntamento in qualsiasi momento, anche poco prima.\n\n' +
          'Scrivi una domanda o scegli un numero per approfondire!'
      };
    }
    return {
      role: 'assistant',
      content: '⚖️ **AVVISO IMPORTANTE - DISCLAIMER LEGALE**\n\n' +
        'Prima di procedere, ti informiamo che:\n\n' +
        '• Questo assistente virtuale **NON sostituisce in alcun modo** il parere del medico oculista\n' +
        '• Le informazioni fornite hanno **esclusivo scopo informativo** e divulgativo\n' +
        '• PRIME VISION **declina ogni responsabilità** per decisioni prese sulla base delle risposte di questo chatbot\n' +
        '• Per diagnosi, terapie e consigli medici personalizzati è **necessaria una visita specialistica**\n\n' +
        '**Continuando a utilizzare questo servizio, accetti le condizioni sopra descritte.**\n\n' +
        '---\n\n' +
        'Ciao! Sono l\'assistente virtuale di PRIME VISION. 👋\n\n' +
        '**Chi siamo?**\n' +
        'PRIME VISION è un centro oculistico d\'eccellenza che nasce con una missione: costruire salute attraverso la prevenzione.\n\n' +
        '**Come posso aiutarti?**\n' +
        '• 📋 FAQ - Domande frequenti\n' +
        '• 📦 Informazioni sui pacchetti e abbonamenti\n' +
        '• 🏥 Domande sui servizi\n' +
        '• 📅 Prenotare un appuntamento\n\n' + 
        (!isLoggedIn ? '💡 Effettua il login per compilare l\'anamnesi e prenotare!' : '📅 Scrivi "prenota" per prenotare un appuntamento!')
    };
  };

  const startAnamnesisWithReason = async (reason) => {
    setVisitReason(reason.id);
    setShowReasonSelector(false);
    anamnesisStartedRef.current = true; // Impedisce che il selettore riappaia
    setLoading(true);
    
    // Start chat with the selected reason - call backend to get personalized greeting
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const token = localStorage.getItem('token');
    const newSessionId = `anamnesis_${Date.now()}`;
    setSessionId(newSessionId);
    
    // Show initial message while waiting for AI response
    setMessages([{
      role: 'assistant',
      content: `Perfetto! Motivo: **${reason.label}**\n\nInizio la raccolta delle informazioni...`
    }]);
    
    try {
      // Call backend to get personalized anamnesis greeting
      const response = await fetch(`${BACKEND_URL}/api/chat`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          message: `Inizia l'anamnesi per motivo: ${reason.label}`,
          session_id: newSessionId,
          mode: 'anamnesis',
          patient_id: user?.id || null,
          visit_reason: reason.id
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setMessages([{
          role: 'assistant',
          content: data.response
        }]);
      } else {
        setMessages([{
          role: 'assistant',
          content: `Perfetto! Motivo: **${reason.label}**\n\nInizio la raccolta delle informazioni. Le farò poche domande mirate.\n\nCome si chiama?`
        }]);
      }
    } catch (error) {
      console.error('Error starting anamnesis:', error);
      setMessages([{
        role: 'assistant',
        content: `Perfetto! Motivo: **${reason.label}**\n\nInizio la raccolta delle informazioni. Le farò poche domande mirate.\n\nCome si chiama?`
      }]);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, bookingStep]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([getInitialMessage(mode)]);
    }
  }, [isOpen, isLoggedIn]);

  // Load available dates when booking mode starts
  useEffect(() => {
    if (bookingStep === 'date') {
      loadAvailableDates();
    }
  }, [bookingStep]);

  const loadAvailableDates = async () => {
    setBookingLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/chat/booking/available-dates`);
      const data = await response.json();
      setAvailableDates(data.available_dates || []);
    } catch (error) {
      console.error('Error loading dates:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: '❌ Errore nel caricamento delle date disponibili. Riprova tra poco.' 
      }]);
    } finally {
      setBookingLoading(false);
    }
  };

  const loadAvailableSlots = async (date) => {
    setBookingLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/chat/booking/slots/${date}`);
      const data = await response.json();
      setAvailableSlots(data.slots || []);
    } catch (error) {
      console.error('Error loading slots:', error);
    } finally {
      setBookingLoading(false);
    }
  };

  const resetChat = (newMode) => {
    if (newMode === 'anamnesis' && (!isLoggedIn || userRole !== 'patient')) {
      return;
    }
    if (newMode === 'booking' && !isLoggedIn) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: '⚠️ Per prenotare un appuntamento devi prima effettuare il login.\n\n👉 Clicca su "Accedi" nel menu principale.' 
      }]);
      return;
    }
    
    // For anamnesis, start with simple questions (no preset options)
    if (newMode === 'anamnesis') {
      setMode(newMode);
      setShowReasonSelector(false); // Non mostrare più il selettore preimpostato
      setMessages([getInitialMessage(newMode)]);
      setSessionId(`anamnesis_${Date.now()}`);
      setHasReport(false);
      setLastReport('');
      setVisitReason(null);
      setAnamnesisQuestion(0); // Reset domande
      setPatientName('');
      setPatientReason('');
      anamnesisStartedRef.current = true;
      return;
    }
    
    setMode(newMode);
    setSessionId(null);
    setMessages([getInitialMessage(newMode)]);
    setHasReport(false);
    setLastReport('');
    setVisitReason(null);
    setShowReasonSelector(false);
    setAnamnesisQuestion(0);
    setPatientName('');
    setPatientReason('');
    anamnesisStartedRef.current = false;
    
    // Reset booking state
    setBookingStep(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setSelectedReason('');
    setAvailableDates([]);
    setAvailableSlots([]);
  };

  const startBookingMode = () => {
    if (!isLoggedIn) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: '⚠️ Per prenotare un appuntamento devi prima effettuare il login.\n\n👉 Clicca su "Accedi" nel menu principale.' 
      }]);
      return;
    }
    // Invece di avviare il flusso guidato, invia direttamente il link
    setMessages(prev => [...prev, { 
      role: 'assistant', 
      content: '📅 **Prenota il tuo appuntamento**\n\nPuoi prenotare direttamente dalla nostra pagina dedicata:\n\n👉 **[Clicca qui per prenotare](/booking)**\n\nTroverai tutte le date e gli orari disponibili!',
      showBookingLink: true
    }]);
  };

  const handleDateSelect = async (dateObj) => {
    setSelectedDate(dateObj);
    setMessages(prev => [...prev, 
      { role: 'user', content: `📅 ${dateObj.display}` },
      { role: 'assistant', content: `Ottima scelta! Ora seleziona un orario per ${dateObj.display}:` }
    ]);
    setBookingStep('time');
    await loadAvailableSlots(dateObj.date);
  };

  const handleTimeSelect = (slotObj) => {
    setSelectedTime(slotObj);
    setMessages(prev => [...prev, 
      { role: 'user', content: `🕐 ${slotObj.time} (${slotObj.period})` },
      { role: 'assistant', content: 'Perfetto! Qual è il motivo della visita?' }
    ]);
    setBookingStep('reason');
  };

  const handleReasonSelect = (reason) => {
    setSelectedReason(reason);
    setMessages(prev => [...prev, 
      { role: 'user', content: `📋 ${reason}` },
      { role: 'assistant', content: `Riepilogo prenotazione:\n\n📅 Data: ${selectedDate.display}\n🕐 Ora: ${selectedTime.time}\n📋 Motivo: ${reason}\n\nConfermi la prenotazione?` }
    ]);
    setBookingStep('confirm');
  };

  const handleConfirmBooking = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: '❌ Sessione scaduta. Effettua nuovamente il login.' 
      }]);
      return;
    }

    setBookingLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/chat/booking/create`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          date: selectedDate.date,
          time_slot: selectedTime.time,
          reason: selectedReason
        })
      });

      const data = await response.json();

      if (response.ok) {
        setMessages(prev => [...prev, 
          { role: 'user', content: '✅ Confermo' },
          { role: 'assistant', content: `🎉 Prenotazione confermata!\n\n📅 ${data.appointment.display_date}\n🕐 Ore ${data.appointment.time}\n📋 ${data.appointment.reason}\n\nRiceverai una conferma via email.\n\n💡 Puoi visualizzare i tuoi appuntamenti nella tua dashboard paziente.\n\nPosso aiutarti con altro?` }
        ]);
        setBookingStep(null);
        setMode('general');
      } else {
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: `❌ ${data.detail || 'Errore nella prenotazione. Riprova.'}` 
        }]);
      }
    } catch (error) {
      console.error('Booking error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: '❌ Errore di connessione. Riprova tra poco.' 
      }]);
    } finally {
      setBookingLoading(false);
    }
  };

  const cancelBooking = () => {
    setBookingStep(null);
    setMode('general');
    setSelectedDate(null);
    setSelectedTime(null);
    setSelectedReason('');
    setMessages(prev => [...prev, { 
      role: 'assistant', 
      content: 'Prenotazione annullata. Come posso aiutarti?' 
    }]);
  };

  const [anamnesisQuestion, setAnamnesisQuestion] = useState(0); // 0: nome, 1: motivo, 2: done
  const [patientName, setPatientName] = useState('');
  const [patientReason, setPatientReason] = useState('');

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    
    // Check for booking intent
    const bookingKeywords = ['prenota', 'prenotare', 'appuntamento', 'visita'];
    if (mode === 'general' && bookingKeywords.some(k => userMessage.toLowerCase().includes(k))) {
      setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
      startBookingMode();
      return;
    }
    
    // Gestione anamnesi semplificata: 2 domande → link
    if (mode === 'anamnesis' && anamnesisQuestion < 2) {
      setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
      
      if (anamnesisQuestion === 0) {
        // Prima risposta: nome
        setPatientName(userMessage);
        setAnamnesisQuestion(1);
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: `Piacere ${userMessage}! 👋\n\n**Qual è il motivo della tua visita?**\n\n(es: controllo, problemi di vista, occhio secco, ecc.)`
        }]);
        return;
      } else if (anamnesisQuestion === 1) {
        // Seconda risposta: motivo
        setPatientReason(userMessage);
        setAnamnesisQuestion(2);
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: `Perfetto! Ho registrato:\n\n👤 **Nome:** ${patientName}\n📋 **Motivo:** ${userMessage}\n\nOra puoi completare l'anamnesi guidata per permettere al medico di conoscerti meglio prima della visita.\n\n**Ci vorranno meno di 5 minuti!**`,
          showAnamnesisLink: true
        }]);
        return;
      }
    }

    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      const response = await fetch(`${BACKEND_URL}/api/chat`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId,
          mode: mode,
          patient_id: user?.id || null,
          visit_reason: visitReason || null
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        setSessionId(data.session_id);
        setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
        
        // Generate quick replies based on bot response
        if (mode === 'anamnesis') {
          const replies = generateQuickReplies(data.response);
          setQuickReplies(replies);
        }
        
        if (data.has_report || data.response.includes('REPORT ANAMNESI') || data.response.includes('📋 REPORT')) {
          setHasReport(true);
          setLastReport(data.response);
        }
      } else {
        setMessages(prev => [...prev, { role: 'assistant', content: 'Mi dispiace, si è verificato un errore. Riprova più tardi.' }]);
        setQuickReplies([]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { role: 'assistant', content: 'Errore di connessione. Verifica la tua connessione internet.' }]);
      setQuickReplies([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle quick reply selection
  const selectQuickReply = (reply) => {
    setInput(reply);
    setQuickReplies([]);
    // Auto-send the message
    setTimeout(() => {
      const fakeEvent = { preventDefault: () => {} };
      sendMessage(fakeEvent);
    }, 100);
  };

  const saveAnamnesis = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: '⚠️ Per salvare l\'anamnesi nel fascicolo devi prima effettuare il login.\n\n👉 Vai su "Accedi" nel menu, fai login con le tue credenziali, e poi torna qui per salvare.' 
      }]);
      return;
    }

    if (!sessionId) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: '⚠️ Sessione non valida. Ricarica la pagina e riprova.' 
      }]);
      return;
    }

    if (!lastReport) {
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: '⚠️ Nessun report da salvare. Completa prima tutte le domande dell\'anamnesi.' 
      }]);
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/chat/save-anamnesis`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          session_id: sessionId,
          report_text: lastReport
        })
      });

      const data = await response.json();
      
      if (response.ok) {
        let successMessage = '✅ Anamnesi salvata con successo nel tuo fascicolo sanitario!';
        if (data.critical_tags && data.critical_tags.length > 0) {
          successMessage += '\n\n⚠️ TAG CRITICI registrati:\n' + data.critical_tags.join('\n');
        }
        successMessage += '\n\n📁 Puoi visualizzare l\'anamnesi nella sezione "Anamnesi" della tua dashboard paziente.';
        successMessage += '\n\n📅 Vuoi prenotare un appuntamento? Scrivi "prenota"!';
        setMessages(prev => [...prev, { role: 'assistant', content: successMessage }]);
        setHasReport(false);
      } else {
        const errorMsg = data.detail || 'Errore sconosciuto';
        setMessages(prev => [...prev, { role: 'assistant', content: `❌ Errore nel salvataggio: ${errorMsg}` }]);
      }
    } catch (error) {
      console.error('Save error:', error);
      let errorMessage = '❌ Errore di connessione.';
      if (error.message) {
        errorMessage += ` (${error.message})`;
      }
      errorMessage += '\n\nAssicurati di essere connesso a internet e riprova.';
      setMessages(prev => [...prev, { role: 'assistant', content: errorMessage }]);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Booking UI Components
  const renderBookingInterface = () => {
    if (!bookingStep) return null;

    return (
      <div className="p-3 border-t" style={{ background: '#F0FDF4', borderColor: '#D1FAE5' }}>
        {bookingLoading ? (
          <div className="flex items-center justify-center py-4">
            <div className="w-6 h-6 border-2 border-green-300 border-t-green-600 rounded-full animate-spin"></div>
            <span className="ml-2 text-sm text-green-700">Caricamento...</span>
          </div>
        ) : (
          <>
            {/* Date Selection */}
            {bookingStep === 'date' && (
              <div className="space-y-2">
                <div className="text-xs font-medium text-green-700 mb-2 flex items-center gap-1">
                  <Calendar className="w-3 h-3" />
                  Date disponibili (prossimi 14 giorni)
                </div>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {availableDates.length > 0 ? (
                    availableDates.map((d) => (
                      <button
                        key={d.date}
                        onClick={() => handleDateSelect(d)}
                        className="w-full text-left p-2 rounded-lg text-sm hover:bg-green-100 transition-all flex justify-between items-center"
                        style={{ background: '#fff', border: '1px solid #D1FAE5' }}
                        data-testid={`chat-date-${d.date}`}
                      >
                        <span className="font-medium text-[#2A9FCB]">{d.display}</span>
                        <span className="text-xs text-green-600">{d.available_slots} slot</span>
                      </button>
                    ))
                  ) : (
                    <p className="text-sm text-[#2A9FCB]/70 text-center py-2">
                      Nessuna data disponibile al momento
                    </p>
                  )}
                </div>
                <button
                  onClick={cancelBooking}
                  className="w-full mt-2 py-2 text-xs text-[#2A9FCB]/70 hover:text-[#2A9FCB]"
                >
                  Annulla prenotazione
                </button>
              </div>
            )}

            {/* Time Selection */}
            {bookingStep === 'time' && (
              <div className="space-y-2">
                <div className="text-xs font-medium text-green-700 mb-2 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Orari disponibili - {selectedDate?.display}
                </div>
                
                {/* Morning slots */}
                {availableSlots.filter(s => s.period === 'mattina').length > 0 && (
                  <div className="mb-2">
                    <p className="text-[10px] text-[#2A9FCB]/70 mb-1">Mattina</p>
                    <div className="grid grid-cols-4 gap-1">
                      {availableSlots.filter(s => s.period === 'mattina').map((slot) => (
                        <button
                          key={slot.time}
                          onClick={() => handleTimeSelect(slot)}
                          className="p-2 rounded text-xs font-medium hover:bg-green-100 transition-all"
                          style={{ background: '#fff', border: '1px solid #D1FAE5', color: '#2A9FCB' }}
                          data-testid={`chat-time-${slot.time}`}
                        >
                          {slot.time}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Afternoon slots */}
                {availableSlots.filter(s => s.period === 'pomeriggio').length > 0 && (
                  <div>
                    <p className="text-[10px] text-[#2A9FCB]/70 mb-1">Pomeriggio</p>
                    <div className="grid grid-cols-4 gap-1">
                      {availableSlots.filter(s => s.period === 'pomeriggio').map((slot) => (
                        <button
                          key={slot.time}
                          onClick={() => handleTimeSelect(slot)}
                          className="p-2 rounded text-xs font-medium hover:bg-green-100 transition-all"
                          style={{ background: '#fff', border: '1px solid #D1FAE5', color: '#2A9FCB' }}
                          data-testid={`chat-time-${slot.time}`}
                        >
                          {slot.time}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <button
                  onClick={() => { setBookingStep('date'); setSelectedDate(null); }}
                  className="w-full mt-2 py-2 text-xs text-[#2A9FCB]/70 hover:text-[#2A9FCB] flex items-center justify-center gap-1"
                >
                  <ArrowLeft className="w-3 h-3" />
                  Cambia data
                </button>
              </div>
            )}

            {/* Reason Selection */}
            {bookingStep === 'reason' && (
              <div className="space-y-2">
                <div className="text-xs font-medium text-green-700 mb-2">
                  Motivo della visita
                </div>
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {BOOKING_REASONS.map((reason) => (
                    <button
                      key={reason}
                      onClick={() => handleReasonSelect(reason)}
                      className="w-full text-left p-2 rounded-lg text-sm hover:bg-green-100 transition-all"
                      style={{ background: '#fff', border: '1px solid #D1FAE5' }}
                      data-testid={`chat-reason-${reason}`}
                    >
                      {reason}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => { setBookingStep('time'); setSelectedTime(null); }}
                  className="w-full mt-2 py-2 text-xs text-[#2A9FCB]/70 hover:text-[#2A9FCB] flex items-center justify-center gap-1"
                >
                  <ArrowLeft className="w-3 h-3" />
                  Cambia orario
                </button>
              </div>
            )}

            {/* Confirmation */}
            {bookingStep === 'confirm' && (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <button
                    onClick={handleConfirmBooking}
                    disabled={bookingLoading}
                    className="flex-1 py-2 rounded-lg text-sm font-medium text-white flex items-center justify-center gap-1"
                    style={{ background: '#2A9FCB' }}
                    data-testid="chat-confirm-booking"
                  >
                    <Check className="w-4 h-4" />
                    Conferma
                  </button>
                  <button
                    onClick={cancelBooking}
                    className="flex-1 py-2 rounded-lg text-sm font-medium text-[#2A9FCB]/80 bg-gray-100 hover:bg-gray-200"
                    data-testid="chat-cancel-booking"
                  >
                    Annulla
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Chat Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-20 right-6 z-[9999] w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110"
        style={{ background: 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)' }}
        data-testid="chat-widget-button"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          className={`fixed z-[9999] bg-white shadow-2xl flex flex-col overflow-hidden ${
            isMobile 
              ? 'inset-0 rounded-none' 
              : 'bottom-36 right-4 sm:right-6 w-[calc(100vw-32px)] sm:w-[380px] max-w-[380px] rounded-2xl'
          }`}
          style={{ 
            border: isMobile ? 'none' : '1px solid #E5E7EB',
            height: isMobile ? '100%' : 'min(550px, calc(100vh - 180px))',
            maxHeight: isMobile ? '100%' : 'calc(100vh - 180px)'
          }}
          data-testid="chat-widget-window"
        >
          {/* Header */}
          <div 
            className="p-4"
            style={{ background: 'linear-gradient(135deg, #2A9FCB 0%, #2A9FCB 100%)' }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  {mode === 'anamnesis' ? <ClipboardList className="w-5 h-5 text-white" /> : 
                   mode === 'booking' ? <Calendar className="w-5 h-5 text-white" /> : 
                   <Bot className="w-5 h-5 text-white" />}
                </div>
                <div>
                  <h3 className="font-semibold text-white">
                    {mode === 'anamnesis' ? 'Anamnesi PRIME VISION' : 
                     mode === 'booking' ? 'Prenotazione' :
                     'Assistente PRIME VISION'}
                  </h3>
                  <p className="text-xs text-white/80">
                    {mode === 'anamnesis' ? 'Raccolta dati pre-visita' : 
                     mode === 'booking' ? 'Prenota un appuntamento' :
                     'Assistenza e prenotazioni'}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Mode Tabs */}
            <div className="flex gap-1 mt-3">
              {isLoggedIn && userRole === 'patient' ? (
                <>
                  <button
                    onClick={() => resetChat('anamnesis')}
                    className={`flex-1 py-2 px-1 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-1 ${
                      mode === 'anamnesis' ? 'bg-white text-green-700' : 'bg-white/20 hover:bg-white/30'
                    }`}
                  >
                    <ClipboardList className="w-3 h-3" />
                    Anamnesi
                  </button>
                  <button
                    onClick={() => resetChat('booking')}
                    className={`flex-1 py-2 px-1 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-1 ${
                      mode === 'booking' ? 'bg-white text-green-700' : 'bg-white/20 hover:bg-white/30'
                    }`}
                    data-testid="chat-booking-tab"
                  >
                    <Calendar className="w-3 h-3" />
                    Prenota
                  </button>
                  <button
                    onClick={() => resetChat('faq')}
                    className={`flex-1 py-2 px-1 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-1 ${
                      mode === 'faq' ? 'bg-white text-green-700' : 'bg-white/20 hover:bg-white/30'
                    }`}
                  >
                    <HelpCircle className="w-3 h-3" />
                    FAQ
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => resetChat('general')}
                    className={`flex-1 py-2 px-2 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-1 ${
                      mode === 'general' ? 'bg-white text-green-700' : 'bg-white/20 hover:bg-white/30'
                    }`}
                  >
                    <Bot className="w-3 h-3" />
                    Assistenza
                  </button>
                  <button
                    onClick={() => resetChat('faq')}
                    className={`flex-1 py-2 px-2 rounded-lg text-xs font-medium transition-all flex items-center justify-center gap-1 ${
                      mode === 'faq' ? 'bg-white text-green-700' : 'bg-white/20 hover:bg-white/30'
                    }`}
                  >
                    <HelpCircle className="w-3 h-3" />
                    FAQ
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Anamnesis Reason Selector */}
          {showReasonSelector && mode === 'anamnesis' && (
            <div className="p-4 border-b" style={{ background: '#F0FDF4', borderColor: '#D1FAE5' }}>
              <p className="text-sm font-medium text-green-800 mb-3">Seleziona il motivo della visita:</p>
              <div className="grid grid-cols-2 gap-2">
                {ANAMNESIS_REASONS.map((reason) => (
                  <button
                    key={reason.id}
                    onClick={() => startAnamnesisWithReason(reason)}
                    className="p-2 rounded-lg text-xs font-medium text-left hover:bg-green-100 transition-all flex items-center gap-2"
                    style={{ background: '#fff', border: '1px solid #D1FAE5' }}
                  >
                    <span>{reason.icon}</span>
                    <span className="text-[#2A9FCB]">{reason.label.replace(reason.icon + ' ', '')}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{ background: '#F9FAFB' }}>
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex items-start gap-2 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div 
                    className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      msg.role === 'user' ? 'bg-gray-200' : ''
                    }`}
                    style={msg.role === 'assistant' ? { background: '#2A9FCB' } : {}}
                  >
                    {msg.role === 'user' ? (
                      <User className="w-4 h-4 text-[#2A9FCB]/80" />
                    ) : (
                      mode === 'anamnesis' ? <ClipboardList className="w-4 h-4 text-white" /> : 
                      mode === 'booking' ? <Calendar className="w-4 h-4 text-white" /> :
                      <Bot className="w-4 h-4 text-white" />
                    )}
                  </div>
                  <div
                    className={`p-3 rounded-2xl ${
                      msg.role === 'user'
                        ? 'bg-gray-200 text-[#2A9FCB] rounded-br-md'
                        : 'bg-white shadow-sm rounded-bl-md'
                    }`}
                    style={msg.role === 'assistant' ? { border: '1px solid #E5E7EB' } : {}}
                  >
                    <div 
                      className="text-sm whitespace-pre-wrap"
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
                    />
                    
                    {/* Link per prenotazione */}
                    {msg.showBookingLink && (
                      <a 
                        href="/booking"
                        className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white font-medium text-sm transition-all hover:opacity-90"
                        style={{ background: '#2A9FCB' }}
                        onClick={() => setIsOpen(false)}
                      >
                        <Calendar className="w-4 h-4" />
                        Vai alla Prenotazione
                      </a>
                    )}
                    
                    {/* Link per anamnesi guidata */}
                    {msg.showAnamnesisLink && (
                      <a 
                        href="/dashboard/patient?tab=anamnesis"
                        className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white font-medium text-sm transition-all hover:opacity-90"
                        style={{ background: '#2A9FCB' }}
                        onClick={() => setIsOpen(false)}
                      >
                        <ClipboardList className="w-4 h-4" />
                        Compila Anamnesi Guidata
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {/* Loading indicator */}
            {loading && (
              <div className="flex justify-start">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: '#2A9FCB' }}>
                    <ClipboardList className="w-4 h-4 text-white" />
                  </div>
                  <div className="p-3 bg-white rounded-2xl rounded-bl-md shadow-sm" style={{ border: '1px solid #E5E7EB' }}>
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                      <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Booking Interface (when active) */}
          {renderBookingInterface()}

          {/* Save Report Button */}
          {hasReport && mode === 'anamnesis' && !bookingStep && (
            <div className="px-4 py-2" style={{ background: '#ECFDF5', borderTop: '1px solid #D1FAE5' }}>
              <button
                onClick={saveAnamnesis}
                disabled={saving}
                className="w-full py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all"
                style={{ background: '#2A9FCB', color: 'white' }}
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Salvataggio...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Salva Anamnesi nel Fascicolo
                  </>
                )}
              </button>
            </div>
          )}

          {/* Input (hide when booking step is active) */}
          {!bookingStep && (
            <div className="p-4 bg-white" style={{ borderTop: '1px solid #E5E7EB' }}>
              {/* Quick Replies */}
              {quickReplies.length > 0 && mode === 'anamnesis' && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {quickReplies.map((reply, idx) => (
                    <button
                      key={idx}
                      onClick={() => selectQuickReply(reply)}
                      className="px-3 py-1.5 text-sm rounded-full border transition-all hover:shadow-md"
                      style={{ 
                        borderColor: '#2A9FCB', 
                        color: '#2A9FCB',
                        background: 'white'
                      }}
                    >
                      {reply}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={mode === 'anamnesis' ? "Rispondi alle domande..." : "Scrivi 'prenota' per un appuntamento..."}
                  className="flex-1 p-3 rounded-full bg-gray-100 focus:outline-none focus:ring-2 text-sm"
                  style={{ focusRingColor: '#2A9FCB' }}
                  disabled={loading}
                  data-testid="chat-input"
                />
                <button
                  onClick={sendMessage}
                  disabled={!input.trim() || loading}
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white transition-all disabled:opacity-50"
                  style={{ background: '#2A9FCB' }}
                  data-testid="chat-send-button"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
              {mode === 'anamnesis' && (
                <p className="text-[10px] text-center mt-2 text-[#2A9FCB]/60">
                  Le informazioni raccolte saranno visibili al medico durante la visita
                </p>
              )}
              {mode === 'general' && isLoggedIn && (
                <p className="text-[10px] text-center mt-2 text-[#2A9FCB]/60">
                  Scrivi "prenota" per prenotare un appuntamento
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

// Wrapper component che mostra AI Medico per medici/admin/ortottista, ChatWidget normale per pazienti
export const SmartChatWidget = () => {
  const [userRole, setUserRole] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  useEffect(() => {
    const checkUser = () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');
      
      if (token && storedUser) {
        try {
          const user = JSON.parse(storedUser);
          setUserRole(user.role);
          setIsLoggedIn(true);
        } catch (e) {
          setIsLoggedIn(false);
          setUserRole(null);
        }
      } else {
        setIsLoggedIn(false);
        setUserRole(null);
      }
    };
    
    checkUser();
    // Ricontrolla ogni 2 secondi per aggiornamenti login/logout
    const interval = setInterval(checkUser, 2000);
    window.addEventListener('storage', checkUser);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', checkUser);
    };
  }, []);
  
  // STAFF MEDICO (doctor, admin, ortottista) → Assistente AI Oftalmologico
  // NON mostrare chatbot anamnesi a loro
  if (isLoggedIn && (userRole === 'doctor' || userRole === 'admin' || userRole === 'ortottista')) {
    return <MedicalAIAssistant />;
  }
  
  // PAZIENTI o utenti non loggati → ChatWidget standard (con anamnesi per pazienti)
  return <ChatWidget />;
};

export default ChatWidget;
